import { CaretLeft, FileImage, Image } from "@phosphor-icons/react";
import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useUpdateAccountTier from "../../../hooks/useUpdateAccountTier";
import useIDUpload from "../../../hooks/useIDUpload";
import useUtilityBillUpload from "../../../hooks/useUtilityBillUpload";
import useCreateAlertAccount from "../../../hooks/useCreateAlertAccount";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import { HashLoader } from "react-spinners";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { AnimatePresence, motion } from "framer-motion";

// Map existing compliance_type_1 values to the new endpoint's expected values
const mapExistingIdType = (existing: string | null | undefined): string => {
  if (!existing) return "";
  const v = existing.toLowerCase().replace(/[^a-z]/g, "");
  if (v.includes("passport")) return "InternationalPassport";
  if (v.includes("driver")) return "DriverLicense";
  if (v.includes("national") || v.includes("nin")) return "NationalIDCard";
  return "";
};

// Map new endpoint's identificationType back to the legacy compliance_type_1 label
const mapToLegacyIdType = (identificationType: string): string => {
  switch (identificationType) {
    case "InternationalPassport": return "Passport";
    case "DriverLicense": return "Driver's License";
    case "NationalIDCard": return "National ID Card";
    default: return identificationType;
  }
};

const schema = Yup.object().shape({
  identificationType: Yup.string()
    .required("Please select an ID type")
    .oneOf(
      ["InternationalPassport", "DriverLicense", "NationalIDCard"],
      "Please select a valid ID type"
    ),
  bvn: Yup.string()
    .nullable()
    .transform((v) => (v === "" ? null : v))
    .matches(/^\d{11}$/, "BVN must be exactly 11 digits"),
  address: Yup.string().nullable().max(255, "Address must be at most 255 characters"),
});

const SectionHeader = ({ step, title }: { step: string; title: string }) => (
  <div className="flex items-center gap-3">
    <div className="w-6 h-6 rounded-full bg-deepBlue text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
      {step}
    </div>
    <div className="text-sm font-bold text-gray-600 uppercase tracking-wider">{title}</div>
  </div>
);

const FilePickerField = ({
  label,
  hint,
  file,
  onFileChange,
  onRemove,
  inputRef,
  accept,
}: {
  label: string;
  hint: string;
  file: File | null;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
  accept?: string;
}) => (
  <div className="flex flex-col gap-2 w-full">
    <label className="text-sm font-bold text-gray-700">{label}</label>
    <input
      type="file"
      accept={accept || "image/*,.pdf"}
      onChange={onFileChange}
      ref={inputRef}
      className="hidden"
    />
    <AnimatePresence>
      {file ? (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="flex items-center gap-3 border border-gray-300 p-4 rounded-lg"
        >
          <FileImage size={28} className="text-gray-500 flex-shrink-0" />
          <div className="flex flex-col flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-800 truncate">{file.name}</div>
            <div className="text-sm text-gray-500">
              {(file.size / 1024).toFixed(2)} KB
            </div>
          </div>
          <button
            onClick={onRemove}
            type="button"
            className="text-sm text-gray-500 hover:text-red-500 cursor-pointer transition-all flex-shrink-0"
          >
            Remove
          </button>
        </motion.div>
      ) : (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex p-4 border border-dashed border-gray-300 rounded-lg flex-col items-center justify-center gap-2 hover:bg-gray-50 hover:border-gray-400 transition-all cursor-pointer"
        >
          <div className="flex items-center gap-2 bg-gray-100 p-3 rounded-md">
            <Image size={24} className="text-gray-500" />
          </div>
          <div className="font-medium text-sm">Click to choose file</div>
          <div className="text-xs text-gray-500">{hint}</div>
        </motion.button>
      )}
    </AnimatePresence>
  </div>
);

const UpgradeToTier3 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromSetup = (location.state as { fromSetup?: boolean } | null)?.fromSetup;

  const { refetch, user } = useAuth();

  const identityDocRef = useRef<HTMLInputElement>(null);
  const utilityBillRef = useRef<HTMLInputElement>(null);
  const passportPhotoRef = useRef<HTMLInputElement>(null);

  const [identityDocFile, setIdentityDocFile] = useState<File | null>(null);
  const [utilityBillFile, setUtilityBillFile] = useState<File | null>(null);
  const [passportPhotoFile, setPassportPhotoFile] = useState<File | null>(null);

  const { updateAccountTier, isUpdatingTier, isTierUpdateError, tierUpdateError } =
    useUpdateAccountTier({
      onSuccess() {
        toast.success("Tier upgrade submitted successfully");
        refetch();
        navigate(fromSetup ? "/home" : "/settings/compliance");
      },
      onError(err) {
        toast.error(err?.message || "Tier upgrade failed");
      },
    });

  // Background hooks — fire-and-forget to keep existing compliance flags in sync
  const { uploadID } = useIDUpload();
  const { uploadUtilityBill } = useUtilityBillUpload();

  // Belt-and-suspenders: make sure the Alert account exists before upgrading.
  // createAccount is idempotent, so awaiting it when missing is safe.
  const { createAlertAccountAsync } = useCreateAlertAccount();

  const handleSubmit = async (values: {
    identificationType: string;
    bvn: string;
    address: string;
  }) => {
    // Ensure the Alert account exists before proceeding with the tier upgrade.
    if (user?.account_alert !== 1) {
      try {
        await createAlertAccountAsync();
      } catch {
        // Idempotent backend — ignore and continue with the tier upgrade.
      }
    }

    // Primary call — drives UI feedback and navigation
    updateAccountTier({
      identificationType: values.identificationType,
      identityDocument: {
        file: identityDocFile as File,
        name: identityDocFile?.name,
      },
      utilityBill: {
        file: utilityBillFile as File,
        name: utilityBillFile?.name,
      },
      passportPhoto: {
        file: passportPhotoFile as File,
        name: passportPhotoFile?.name,
      },
      bvn: values.bvn || undefined,
      address: values.address || undefined,
      accountTier: "3",
    });

    // Background calls — keep legacy compliance flags updated (no UI feedback)
    uploadID({
      compliance_type_1: mapToLegacyIdType(values.identificationType),
      compliance_image_1: {
        file: identityDocFile as File,
        name: identityDocFile?.name,
      },
    });

    uploadUtilityBill({
      compliance_type_2: "Utility-Bill",
      compliance_image_2: {
        file: utilityBillFile as File,
        name: utilityBillFile?.name,
      },
    });
  };

  // Pre-populate from existing user data
  const initialValues = {
    identificationType: mapExistingIdType(user?.compliance?.regulatory_id_type),
    bvn: user?.compliance?.bvn || "",
    address: user?.address || "",
  };

  const allFilesSelected = !!identityDocFile && !!utilityBillFile && !!passportPhotoFile;

  return (
    <>
      {isUpdatingTier && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <Formik
        validationSchema={schema}
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ isValid }) => (
          <Form className="flex items-center justify-center gap-10 md:px-5 md:py-5 pb-20 flex-col">
            <div className="flex-col gap-4 flex items-start justify-center w-full max-w-[32rem]">
              <div className="bg-white p-8 border border-gray-200 rounded-xl w-full flex flex-col gap-6">

                {/* Header */}
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="p-2 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300 self-start"
                >
                  <CaretLeft weight="bold" size={20} />
                </button>

                <div className="flex flex-col gap-1">
                  <h1 className="font-medium text-3xl">Upgrade to Tier 3</h1>
                  <div className="text-gray-500 text-sm">
                    Provide your ID, a utility bill, and a passport photo to complete your Tier 3 upgrade.
                  </div>
                </div>

                <div className="flex flex-col gap-6">

                  {/* Section 1: Government ID — type + document grouped together */}
                  <div className="flex flex-col gap-3">
                    <SectionHeader step="1" title="Government-issued ID" />
                    <div className="border border-gray-200 rounded-xl p-4 flex flex-col gap-4">
                      <div className="flex flex-col gap-2 w-full">
                        <label
                          htmlFor="identificationType"
                          className="text-sm font-bold text-gray-700"
                        >
                          ID type
                        </label>
                        <Field
                          as="select"
                          id="identificationType"
                          name="identificationType"
                          className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                        >
                          <option value="" disabled>
                            Select ID type
                          </option>
                          <option value="InternationalPassport">
                            International Passport
                          </option>
                          <option value="DriverLicense">Driver's License</option>
                          <option value="NationalIDCard">National ID Card</option>
                        </Field>
                        <ErrorMessage
                          name="identificationType"
                          component="div"
                          className="text-red-500 text-sm"
                        />
                      </div>

                      <FilePickerField
                        label="Upload your ID document"
                        hint="JPEG, JPG, PNG, PDF — max 10MB"
                        file={identityDocFile}
                        onFileChange={(e) => setIdentityDocFile(e.target.files?.[0] ?? null)}
                        onRemove={() => setIdentityDocFile(null)}
                        inputRef={identityDocRef}
                      />
                    </div>
                  </div>

                  {/* Section 2: Utility Bill */}
                  <div className="flex flex-col gap-3">
                    <SectionHeader step="2" title="Proof of Address" />
                    <div className="border border-gray-200 rounded-xl p-4">
                      <FilePickerField
                        label="Utility bill (not older than 3 months)"
                        hint="JPEG, JPG, PNG, PDF — max 10MB"
                        file={utilityBillFile}
                        onFileChange={(e) => setUtilityBillFile(e.target.files?.[0] ?? null)}
                        onRemove={() => setUtilityBillFile(null)}
                        inputRef={utilityBillRef}
                      />
                    </div>
                  </div>

                  {/* Section 3: Passport Photo */}
                  <div className="flex flex-col gap-3">
                    <SectionHeader step="3" title="Passport Photo" />
                    <div className="border border-gray-200 rounded-xl p-4">
                      <FilePickerField
                        label="Clear face photo (plain background preferred)"
                        hint="JPEG, JPG, PNG — max 10MB"
                        file={passportPhotoFile}
                        onFileChange={(e) => setPassportPhotoFile(e.target.files?.[0] ?? null)}
                        onRemove={() => setPassportPhotoFile(null)}
                        inputRef={passportPhotoRef}
                        accept="image/jpeg,image/jpg,image/png"
                      />
                    </div>
                  </div>

                  {/* Section 4: Additional Details (optional, pre-filled) */}
                  <div className="flex flex-col gap-3">
                    <SectionHeader step="4" title="Additional Details" />
                    <div className="border border-gray-200 rounded-xl p-4 flex flex-col gap-4">
                      <div className="text-xs text-gray-500">
                        These are optional but help verify your identity faster.
                        We've pre-filled what we already have on file.
                      </div>

                      <div className="flex flex-col gap-2 w-full">
                        <label htmlFor="bvn" className="text-sm font-bold text-gray-700">
                          BVN
                        </label>
                        <Field
                          id="bvn"
                          name="bvn"
                          type="text"
                          inputMode="numeric"
                          maxLength={11}
                          placeholder="11-digit Bank Verification Number"
                          className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                        />
                        <ErrorMessage
                          name="bvn"
                          component="div"
                          className="text-red-500 text-sm"
                        />
                      </div>

                      <div className="flex flex-col gap-2 w-full">
                        <label htmlFor="address" className="text-sm font-bold text-gray-700">
                          Residential Address
                        </label>
                        <Field
                          id="address"
                          name="address"
                          type="text"
                          placeholder="Your current home address"
                          className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                        />
                        <ErrorMessage
                          name="address"
                          component="div"
                          className="text-red-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {isTierUpdateError && (
                  <div className="text-red-500 text-sm">{tierUpdateError?.message}</div>
                )}

                <div className="w-full flex gap-5 pt-2">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
                  >
                    <CaretLeft weight="bold" size={20} />
                    <span>Back</span>
                  </button>

                  <button
                    disabled={!isValid || isUpdatingTier || !allFilesSelected}
                    type="submit"
                    className="p-2 py-3 text-center flex-1 bg-deepBlue text-white px-10 border border-deepBlue rounded-xl font-medium w-full disabled:opacity-50"
                  >
                    {isUpdatingTier ? "Submitting..." : "Submit Upgrade"}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default UpgradeToTier3;
